import { ChevronLeft, LogOut, Monitor, Moon, Shield, Sun } from "lucide-react"
import { type ReactNode, useState } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import { canPerform } from "../auth/actions"
import { useAuth } from "../contexts/AuthContext"
import { customerOverviewPath } from "../libs/customer-paths"
import { getNavigationConfig } from "../libs/navigation"
import { cn } from "../libs/utils"
import { themePreferenceFromState, useUiStore } from "../stores/useUiStore"
import type { NavItem } from "../types/navigation"
import { BrandMark } from "./ui/brand-mark"
import { Button } from "./ui/button"

/**
 * Drop nav items gated to global admins for callers who aren't global admins.
 * Mirrors the backend gate on tenant-scoped areas (VPP, Grid Services).
 */
function filterNavForAccess(items: NavItem[], isGlobalAdmin: boolean): NavItem[] {
  return items.filter((item) => !item.requiresGlobalAdmin || isGlobalAdmin)
}

interface SidebarProps {
  className?: string
  onCollapse?: (collapsed: boolean) => void
  navigationConfig?: NavItem[]
}

/** 3px accent bar pinned to the sidebar's left edge marking the active item. */
function ActiveBar() {
  return (
    <span
      aria-hidden="true"
      className="absolute bottom-[9px] left-[-12px] top-[9px] w-[3px] rounded-full bg-accent"
    />
  )
}

export function Sidebar({ className, onCollapse, navigationConfig: navConfig }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const location = useLocation()
  const { customerId } = useParams<{ customerId: string }>()
  const { user, updatePreferences } = useAuth()
  const isGlobalAdmin = user?.isGlobalAdmin ?? false
  const canUseCustomerApp = canPerform(user?.platformRole, "app.customers")
  // Match /admin as a path segment so sibling routes aren't misclassified.
  const isAdmin = location.pathname === "/admin" || location.pathname.startsWith("/admin/")
  const theme = useUiStore((s) => s.theme)
  const themeSource = useUiStore((s) => s.themeSource)
  const setTheme = useUiStore((s) => s.setTheme)
  const toggleTheme = useUiStore((s) => s.toggleTheme)
  const resetToSystem = useUiStore((s) => s.resetToSystem)

  const persistThemePreference = (nextTheme: typeof theme, nextSource: typeof themeSource) => {
    updatePreferences({ theme: themePreferenceFromState(nextTheme, nextSource) })
  }

  const chooseTheme = (nextTheme: "light" | "dark") => {
    setTheme(nextTheme)
    persistThemePreference(nextTheme, "user")
  }

  const chooseSystemTheme = () => {
    resetToSystem()
    persistThemePreference(useUiStore.getState().theme, "system")
  }

  const toggleThemeChoice = () => {
    toggleTheme()
    const { theme: nextTheme, themeSource: nextSource } = useUiStore.getState()
    persistThemePreference(nextTheme, nextSource)
  }

  // Use provided nav config or generate from customerId, then drop entries the
  // caller can't access (tenant-scoped areas are global-admin-only for now).
  const navigation = filterNavForAccess(
    navConfig || (customerId ? getNavigationConfig(customerId) : []),
    isGlobalAdmin
  )
  const navItems = navigation.filter((item) => !item.footer)
  const footerItems = navigation.filter((item) => item.footer)

  const toggleCollapse = () => {
    const next = !isCollapsed
    setIsCollapsed(next)
    onCollapse?.(next)
  }

  const matches = (path: string) => {
    // Index routes ("/admin") require an exact match; everything else also
    // matches nested detail routes (e.g. .../assets/sites/:id → Sites).
    if (path === "/" || path === "/admin") return location.pathname === path
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  /**
   * Only the MOST specific match highlights. "/admin/reports" is a prefix of
   * "/admin/reports/bidding", so a plain prefix test lit both Reports and
   * Bidding Report at once.
   */
  const bestMatch = [...navItems, ...footerItems]
    .map((i) => i.path)
    .filter(matches)
    .sort((a, b) => b.length - a.length)[0]

  const isActive = (path: string) => path === bestMatch

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path)
    const Icon = item.icon
    return (
      <Link
        key={item.path}
        to={item.path}
        aria-current={active ? "page" : undefined}
        title={isCollapsed ? item.label : undefined}
        className={cn(
          "relative flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-[13.5px] font-medium text-content-2 transition-colors hover:bg-surface-2 hover:text-content",
          active && "bg-accent-soft text-content",
          // Sub-items sit indented under their parent; when collapsed the
          // sidebar is icons-only, so the indent would just misalign them.
          item.sub && !isCollapsed && "ml-3 pl-6 text-[13px]",
          isCollapsed && "justify-center px-2"
        )}
      >
        {active && <ActiveBar />}
        {Icon && <Icon className="size-[18px] shrink-0" strokeWidth={1.8} />}
        {!isCollapsed && <span className="truncate">{item.label}</span>}
      </Link>
    )
  }

  // Walk the visible items, emitting a section header each time the section
  // changes (skipped while collapsed, where only icons show).
  const navContent: ReactNode[] = []
  let lastSection: string | undefined
  for (const item of navItems) {
    if (!isCollapsed && item.section && item.section !== lastSection) {
      navContent.push(
        <div
          key={`section-${item.section}`}
          className="px-3 pb-1.5 pt-3.5 text-[10.5px] font-medium uppercase tracking-[0.12em] text-content-3"
        >
          {item.section}
        </div>
      )
    }
    lastSection = item.section
    navContent.push(renderNavItem(item))
  }

  return (
    <aside
      className={cn(
        "sticky top-0 flex h-screen flex-col border-r border-hairline bg-bg-2 transition-all duration-300",
        isCollapsed ? "w-sidebar-collapsed" : "w-sidebar",
        className
      )}
      aria-label="Main navigation"
    >
      {/* Logo — GRIDBOX mark + wordmark */}
      <Link
        to={isAdmin ? "/admin" : customerId ? customerOverviewPath(customerId) : "/customer-picker"}
        aria-label="GRIDBOX home"
        className={cn(
          // btn-unstyled exempts the logo from the global 44px touch-target rule,
          // which would otherwise force inline-flex / justify-center and override
          // the justify-start below (keeping the mark + wordmark left-justified).
          "btn-unstyled flex items-center gap-[11px] pb-[18px] pt-[22px]",
          // 22px left inset matches GRIDBOX.dc.html (padding:22px 22px 18px); the
          // explicit flex-start keeps the mark + wordmark left-justified, not centered.
          isCollapsed ? "justify-center px-0" : "justify-start px-[22px]"
        )}
      >
        <BrandMark className="size-11 shrink-0 object-contain" />
        {!isCollapsed && (
          <div>
            {/*
              The wordmark sits beside the Nuvve mark, whose letterforms are
              geometric sans. Merriweather (font-display) fights that, so the
              wordmark uses the brand sans with the generous kerning the brand
              sheet specifies for Montserrat.
            */}
            <div className="font-sans text-[15px] font-semibold uppercase leading-none tracking-[0.22em] text-accent">
              Gridbox
            </div>
          </div>
        )}
      </Link>

      {/* Navigation */}
      <nav
        className="sidebar-scrollbar flex flex-1 flex-col gap-[2px] overflow-y-auto px-3 py-1.5"
        aria-label="Primary"
      >
        {isAdmin && !isCollapsed && (
          <div className="admin-console-badge mb-2 flex items-center gap-2 rounded-[9px] border px-3 py-2">
            <Shield
              className="size-[15px] shrink-0"
              strokeWidth={1.9}
              style={{ color: "var(--admin)" }}
            />
            <span
              className="text-[11px] font-bold tracking-[0.14em]"
              style={{ color: "var(--admin)" }}
            >
              ADMIN CONSOLE
            </span>
          </div>
        )}
        {navContent}
      </nav>

      {/* Footer: theme toggle · settings/exit · collapse */}
      <div className="space-y-2 border-t border-hairline p-3">
        {/* Theme toggle: segmented Dark/Light/System when expanded, single icon when collapsed */}
        {isCollapsed ? (
          <button
            type="button"
            onClick={toggleThemeChoice}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            className="btn-unstyled flex w-full items-center justify-center rounded-control bg-surface-3 py-2 text-content-2 transition-colors hover:text-content"
          >
            {theme === "dark" ? (
              <Sun className="size-4 shrink-0" />
            ) : (
              <Moon className="size-4 shrink-0" />
            )}
          </button>
        ) : (
          <div className="flex rounded-control bg-surface-3 p-[3px]">
            <button
              type="button"
              onClick={() => chooseTheme("dark")}
              data-active={themeSource === "user" && theme === "dark"}
              aria-pressed={themeSource === "user" && theme === "dark"}
              aria-label="Dark theme"
              className="btn-unstyled flex flex-1 items-center justify-center gap-1.5 rounded-[8px] py-[7px] text-[12.5px] font-medium text-content-2 transition-colors data-[active=true]:bg-surface data-[active=true]:text-content data-[active=true]:shadow-sm"
            >
              <Moon className="size-[15px] shrink-0" strokeWidth={1.8} />
              <span>Dark</span>
            </button>
            <button
              type="button"
              onClick={() => chooseTheme("light")}
              data-active={themeSource === "user" && theme === "light"}
              aria-pressed={themeSource === "user" && theme === "light"}
              aria-label="Light theme"
              className="btn-unstyled flex flex-1 items-center justify-center gap-1.5 rounded-[8px] py-[7px] text-[12.5px] font-medium text-content-2 transition-colors data-[active=true]:bg-surface data-[active=true]:text-content data-[active=true]:shadow-sm"
            >
              <Sun className="size-[15px] shrink-0" strokeWidth={1.8} />
              <span>Light</span>
            </button>
            <button
              type="button"
              onClick={chooseSystemTheme}
              data-active={themeSource === "system"}
              aria-pressed={themeSource === "system"}
              aria-label="System theme"
              className="btn-unstyled flex flex-1 items-center justify-center gap-1.5 rounded-[8px] py-[7px] text-[12.5px] font-medium text-content-2 transition-colors data-[active=true]:bg-surface data-[active=true]:text-content data-[active=true]:shadow-sm"
            >
              <Monitor className="size-[15px] shrink-0" strokeWidth={1.8} />
              <span>Auto</span>
            </button>
          </div>
        )}

        {/* Bottom-block actions (Company settings · Exit to customer app). Inside a
            <nav> so they keep the compact nav-item height (exempt from the global
            44px touch-target rule) and the active-state treatment. */}
        {(footerItems.length > 0 || isAdmin) && (
          <nav aria-label="Settings">
            {footerItems.map(renderNavItem)}
            {isAdmin && canUseCustomerApp && (
              <Link
                to="/"
                title={isCollapsed ? "Exit to customer app" : undefined}
                className={cn(
                  "relative flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-[13.5px] font-medium text-content transition-colors hover:bg-surface-2 hover:text-content",
                  isCollapsed && "justify-center px-2"
                )}
              >
                <LogOut className="size-[18px] shrink-0" strokeWidth={1.8} />
                {!isCollapsed && <span>Exit to customer app</span>}
              </Link>
            )}
          </nav>
        )}

        {/* Collapse toggle */}
        <Button
          variant="secondary"
          size={isCollapsed ? "icon" : "default"}
          onClick={toggleCollapse}
          className={cn("w-full gap-2 rounded-pill", isCollapsed && "justify-start")}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft className={cn("size-5 transition-transform", isCollapsed && "rotate-180")} />
          {!isCollapsed && <span className="text-[13.5px]">Hide menu</span>}
        </Button>
      </div>
    </aside>
  )
}
